import { Link } from "react-router-dom";
import { Card, DataState, PageHeader, StatCard } from "@vms/ui-web";
import { useQuery } from "../../api/queries";
import { useOrgContext } from "../../context/OrgContext";

interface Alert {
  hubId?: string;
  venueId?: string;
  venueName: string | null;
  organisationName: string | null;
  lastSeenAt?: string | null;
  expiresAt?: string;
  termEndsAt?: string;
}

interface OverviewPayload {
  activeOrgs: number;
  activeVenues: number;
  venuesCreatedThisWeek: number;
  hubsOnline: number;
  hubsTotal: number;
  hubsOfflineLong: number;
  sessionsToday: number;
  certsExpiringSoon: number;
  pendingTransfers: number;
  dlqDepth: number;
  termsExpiringSoon: number;
  alerts: {
    offlineHubs: Alert[];
    expiringCerts: Alert[];
    expiringTerms: Alert[];
  };
}

export function OperationsOverview() {
  const overview = useQuery<OverviewPayload>("/api/admin/operations-overview");
  const data = overview.data;
  const { orgs } = useOrgContext();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operations Overview"
        subtitle="Cross-org ops-health snapshot."
      />

      <DataState loading={overview.loading} error={overview.error}>
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Active orgs" value={data.activeOrgs} />
            <StatCard
              label="Active venues"
              value={data.activeVenues}
              hint={data.venuesCreatedThisWeek > 0 ? `+${data.venuesCreatedThisWeek} this week` : undefined}
            />
            <StatCard
              label="Hubs online"
              value={`${data.hubsOnline} / ${data.hubsTotal}`}
              tone={data.hubsTotal > 0 && data.hubsOnline < data.hubsTotal ? "amber" : "default"}
            />
            <StatCard label="Sessions today" value={data.sessionsToday} />
            <StatCard
              label="Certs expiring 7d"
              value={data.certsExpiringSoon}
              tone={data.certsExpiringSoon > 0 ? "red" : "default"}
            />
            <StatCard
              label="Webhook DLQ"
              value={data.dlqDepth}
              tone={data.dlqDepth > 0 ? "amber" : "default"}
              hint={data.pendingTransfers > 0 ? `${data.pendingTransfers} pending transfer${data.pendingTransfers === 1 ? "" : "s"}` : undefined}
            />
            <StatCard
              label="Terms expiring 30d"
              value={data.termsExpiringSoon}
              tone={data.termsExpiringSoon > 0 ? "amber" : "default"}
            />
          </div>
        )}
      </DataState>

      {data &&
        (data.alerts.offlineHubs.length > 0 ||
          data.alerts.expiringCerts.length > 0 ||
          data.alerts.expiringTerms.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.alerts.offlineHubs.length > 0 && (
            <Card className="overflow-hidden border-amber-200">
              <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 font-semibold text-amber-900">
                Hubs offline &gt; 24 h
              </div>
              <ul className="divide-y divide-amber-50">
                {data.alerts.offlineHubs.map((h) => (
                  <li key={h.hubId} className="px-5 py-2 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{h.venueName ?? "—"}</div>
                      <div className="text-xs text-gray-500">{h.organisationName ?? "—"}</div>
                    </div>
                    <div className="text-xs text-amber-700">
                      {h.lastSeenAt ? `last ${new Date(h.lastSeenAt).toLocaleString()}` : "never"}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {data.alerts.expiringCerts.length > 0 && (
            <Card className="overflow-hidden border-red-200">
              <div className="px-5 py-3 border-b border-red-100 bg-red-50 font-semibold text-red-900">
                Certs expiring within 7 days
              </div>
              <ul className="divide-y divide-red-50">
                {data.alerts.expiringCerts.map((c) => (
                  <li key={c.venueId} className="px-5 py-2 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{c.venueName ?? "—"}</div>
                      <div className="text-xs text-gray-500">{c.organisationName ?? "—"}</div>
                    </div>
                    <div className="text-xs text-red-700">
                      expires {c.expiresAt ? new Date(c.expiresAt).toLocaleString() : "—"}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 py-2 text-right">
                <Link to="/admin/licences" className="text-xs font-medium text-primary hover:underline">
                  Open licences →
                </Link>
              </div>
            </Card>
          )}
          {data.alerts.expiringTerms.length > 0 && (
            <Card className="overflow-hidden border-amber-300">
              <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 font-semibold text-amber-900">
                Terms expiring within 30 days
              </div>
              <ul className="divide-y divide-amber-50">
                {data.alerts.expiringTerms.map((t) => (
                  <li key={t.venueId} className="px-5 py-2 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{t.venueName ?? "—"}</div>
                      <div className="text-xs text-gray-500">{t.organisationName ?? "—"}</div>
                    </div>
                    <div className="text-xs text-amber-800">
                      ends {t.termEndsAt ? new Date(t.termEndsAt).toLocaleDateString() : "—"}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 py-2 text-right">
                <Link to="/admin/licences" className="text-xs font-medium text-primary hover:underline">
                  Open licences →
                </Link>
              </div>
            </Card>
          )}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Organisations</h2>
          <Link
            to="/admin/organisations"
            className="text-sm font-medium text-primary hover:underline"
          >
            Manage →
          </Link>
        </div>
        {orgs.length === 0 ? (
          <div className="px-5 py-6 text-sm text-gray-500">No organisations yet.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {orgs.map((org) => (
              <li key={org.id}>
                <Link
                  to={`/org/${org.slug}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium text-gray-900">{org.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{org.slug}</div>
                  </div>
                  <div className="text-xs text-gray-400">Open as org →</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
